import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { Cell, ValueType } from 'exceljs';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

/**
 * converts date format to timestamp
 * @param dateString string in MM/DD/YYYY, M/D/YY, M/D/YYYY, MM/DD/YY format
 * @returns utc timestamp in seconds
 */
export const getTimestampFromUtcDate = (dateString: string) => {
  const formats = ['MM/DD/YYYY', 'MM/DD/YY', 'M/D/YY', 'M/D/YYYY'];
  for (let i = 0; i < formats.length; i += 1) {
    const parsedDate = dayjs.utc(dateString, formats[i], true);
    if (parsedDate.isValid()) {
      // dayjs.valueOf return time in milliseconds
      return parsedDate.valueOf() / 1000;
    }
  }
  return undefined;
};

/**
 * converts unix timestamp to date in given format, default format is 'MM/DD/YYYY'
 * @param timestampEpoch number in unix seconds or milliseconds
 * @param format string date format
 * @returns date as per input param format
 */
export const getUtcDateWithFormat = (timestampEpoch: number, format = 'MM/DD/YYYY') => {
  // dayjs.unix() will convert unix timestamp (in seconds only) to a Dayjs object,
  // if date is in milliseconds, convert into seconds first
  let timestamp = timestampEpoch;
  if (timestampEpoch.toString().length === 13) {
    timestamp = timestampEpoch / 1000;
  }
  return dayjs.utc(dayjs.unix(timestamp)).format(format);
};

/**
 * Get Timestamp in milliseconds
 * @returns number
 */

export const dateComparator = (date1: string | undefined, date2: string | undefined) => {
  const d1 = dayjs.utc(date1, 'MM/DD/YYYY');
  const d2 = dayjs.utc(date2, 'MM/DD/YYYY');
  if (!date1 && !date2) return 0;
  if (!date1 || !d1.isValid()) return -1;
  if (!date2 || !d2.isValid()) return 1;
  return d1.unix() - d2.unix();
};

// TODO: Note this is a temporary fix. A long term fix will be performed in
// https://sim.amazon.com/issues/Aztech-4915 which is BTL for M1 fast follow
// TL;DR, we should use epochTime at all places for data compare, only
// show the date value using a DateValueFormatter, not convert it in
// valueSetter or valueGetter
export const dateFilterComparator = (filterValue: string | undefined, cellValue: string | undefined) => {
  if (cellValue && filterValue) {
    const cellDate = dayjs.utc(cellValue);
    const filterDate = dayjs.utc(filterValue);
    // compare year
    if (filterDate.isAfter(cellDate, 'year')) {
      return -1;
    }
    if (filterDate.isBefore(cellDate, 'year')) {
      return 1;
    }
    // compare month
    if (filterDate.isAfter(cellDate, 'month')) {
      return -1;
    }
    if (filterDate.isBefore(cellDate, 'month')) {
      return 1;
    }
    // compare day
    if (filterDate.isAfter(cellDate, 'day')) {
      return -1;
    }
    if (filterDate.isBefore(cellDate, 'day')) {
      return 1;
    }
  }
  return 0;
};

export const getUnixTimestampFromDateString = (input: string | undefined): number | undefined => {
  if (!input) {
    return undefined;
  }
  const parts = input.split('/').map((part) => (part.length === 1 ? `0${part}` : part));
  const formattedInput = parts.join('/');
  const date = dayjs.utc(formattedInput, 'MM/DD/YYYY');
  if (!date.isValid()) {
    return undefined;
  }
  return date.unix();
};

/**
 * Returns UTC timestamp for excel cell based on cellType
 * @param excelCell
 * @returns timestamp
 */
export const getUtcTimestampFromExcelCell = (excelCell: Cell) => {
  switch (excelCell.type) {
    case ValueType.Date: {
      return dayjs.utc(excelCell.value as Date).unix();
    }
    case ValueType.String: {
      return getTimestampFromUtcDate(excelCell.text);
    }
    default: {
      console.groupEnd();
      return undefined;
    }
  }
};

/**
 * Converts a date value (string or number) to a long timestamp in milliseconds
 * @param dateValue - Can be a string date, unix timestamp in seconds, or unix timestamp in milliseconds
 * @returns timestamp in milliseconds, or undefined if invalid
 */
export const getTimestampFromDateValue = (dateValue: string | number): number | undefined => {
  if (dateValue === null || dateValue === undefined) {
    return undefined;
  }

  // If it's a number (timestamp)
  if (typeof dateValue === 'number') {
    // If it's already in milliseconds (13 digits), return as is
    if (dateValue.toString().length === 13) {
      return dateValue;
    }
    // If it's in seconds (10 digits), convert to milliseconds
    if (dateValue.toString().length === 10) {
      return dateValue * 1000;
    }
    // For other number formats, try to parse as dayjs
    const date = dayjs.utc(dateValue);
    return date.isValid() ? date.valueOf() : undefined;
  }

  // If it's a string
  if (typeof dateValue === 'string') {
    // Try common date formats first
    const formats = ['MM/DD/YYYY', 'MM/DD/YY', 'M/D/YY', 'M/D/YYYY', 'YYYY-MM-DD', 'ISO'];

    for (const format of formats) {
      const parsedDate = format === 'ISO' ? dayjs.utc(dateValue) : dayjs.utc(dateValue, format, true);

      if (parsedDate.isValid()) {
        return parsedDate.valueOf(); // Returns milliseconds
      }
    }

    // Try parsing as a number string (timestamp)
    const numericValue = Number(dateValue);
    if (!isNaN(numericValue)) {
      return getTimestampFromDateValue(numericValue);
    }
  }

  return undefined;
};
